import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isServer } from 'src/utils/isServer';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { Space } from '@sravni/react-design-system';
import { useIsMobile, usePrevious } from '@sravni/react-utils';

import { BackLink } from '../../components/BackLink';
import { ReviewsList } from '../../components/ReviewsList';
import { EXCLUDE_PRODUCT_TYPES, PRODUCT_TYPE } from '../../constants/reviews';
import { handleOrganizationRedirect } from '../../helpers/handleOrganizationRedirect';
import { getCreateReviewBlockDescription, getReviewType, listBackLink } from '../../helpers/reviews';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { useParamsDfp } from '../../hooks/useParamsDfp';
import { OrganizationType } from '../../interfaces/organizations';
import { useBanners, useLoadingBanners } from '../../store/hooks/advertising';
import { useProductsTags } from '../../store/hooks/filters';
import { useOrganizationDetails } from '../../store/hooks/organizations';
import { fetchBanners, setAdProducts } from '../../store/reducers/advertising';
import { fetchSeoIfNeeded } from '../../store/reducers/metadata';
import {
  setActiveOrganizationCount,
  setMenu,
  setOrganizationByAlias,
  setOrganizationPosition,
  setOrganizationRating,
  setOrganizationsList,
} from '../../store/reducers/organizations';
import { setProductDetails } from '../../store/reducers/products';
import { setProductsRating, setRatingSravni, setServicesRating } from '../../store/reducers/ratings';
import {
  setListQuery,
  setReviewsCount,
  setReviewsList,
  setReviewsSqueeze,
  setReviewsStats,
} from '../../store/reducers/reviews';
import styles from '../../styles/page.module.scss';
import type { TReviewListQuery } from '../../types/router';
import { createApplicationError } from '../../utils/errorFabric';
import { serverDataDispatcher } from '../../utils/serverDataDispatcher';

const Banner = dynamic(() => import('../../components/BorderedBanner').then((mod) => mod.BorderedBanner), {
  ssr: false,
});
const OrganizationLinkBlock = dynamic(
  () => import('../../components/OrganizationLinkBlock').then((mod) => mod.OrganizationLinkBlock),
  { ssr: false },
);
const RatingSravni = dynamic(() => import('../../components/RatingSravni').then((mod) => mod.RatingSravni), {
  ssr: false,
});
const SeoBlocks = dynamic(() => import('../../components/SeoBlock').then((mod) => mod.SeoBlocks), { ssr: true });
const SideBlock = dynamic(() => import('../../components/SideBlock').then((mod) => mod.SideBlock), { ssr: false });

const ListPage: NextPage = () => {
  const banners = useBanners();
  const isLoadingBanners = useLoadingBanners();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const productsTags = useProductsTags();
  const router = useRouter() as TRouter<TReviewListQuery>;
  const { query } = router;
  const { type, organization, tag, product } = query;
  const orgDetails = useOrganizationDetails(organization);
  const { prepositionalName } = orgDetails;
  const isWl = useIsWlPage();

  const reviewTypes = getReviewType(type, productsTags);
  const { namesType, itemTypeUrl } = reviewTypes;

  const sideBlockTitle = 'Оставьте отзыв';

  const partnerCard = orgDetails?.partnerCard || orgDetails?.isRecomended;

  const sideBlockDescription = getCreateReviewBlockDescription(itemTypeUrl, namesType?.mode4, prepositionalName);

  const { paramsDfp, paramsForDecoratingBanners } = useParamsDfp({
    tag,
    type,
  });

  const previousParamsDfp = usePrevious(paramsDfp);

  useEffect(() => {
    if (isEqual(previousParamsDfp, paramsDfp)) return;

    dispatch(fetchBanners({ params: paramsDfp, paramsForDecorating: paramsForDecoratingBanners }));
  }, [dispatch, paramsDfp, paramsForDecoratingBanners, previousParamsDfp]);

  const backLink = listBackLink({
    type,
    organization,
    product,
  });

  return (
    <>
      {!isMobile && (
        <BackLink type={reviewTypes.listTypeUrl} customLink={backLink?.href} customLabel={backLink?.label} />
      )}
      <div className={styles.container}>
        <div className={styles.mainColumn}>
          <ReviewsList reviewTypes={reviewTypes} backLink={backLink} />
          {!isWl && isMobile && (isLoadingBanners || banners?.side) && (
            <Banner banner={banners?.side} lazy={true} className={'h-mt-32'} type={BANNER_TYPES.SIDE} />
          )}
        </div>

        <Space className={styles.rightColumn} direction={'vertical'} size={24}>
          {(!orgDetails?.id || orgDetails?.status === 'active') && (
            <SideBlock
              title={sideBlockTitle}
              description={sideBlockDescription}
              titleLevel={4}
              namesType={namesType}
              hasButton
            />
          )}
          {!partnerCard && <RatingSravni type={type} reviewTypes={reviewTypes} />}
          {!isWl && (
            <div className={styles.stickyBlock}>
              {(isLoadingBanners || banners?.side) && (
                <Banner banner={banners?.side} lazy={true} type={BANNER_TYPES.SIDE} />
              )}
            </div>
          )}
        </Space>
      </div>
      <SeoBlocks />
      {!isWl && (isLoadingBanners || banners?.outOfPage) && (
        <Banner banner={banners?.outOfPage} type={BANNER_TYPES.OUT_OF_PAGE} />
      )}
      {orgDetails.ad && orgDetails.type === OrganizationType.SCHOOL && (
        <OrganizationLinkBlock
          organization={orgDetails}
          className={styles.mainColumn}
          rightSideClassName={styles.rightColumn}
        />
      )}
    </>
  );
};

ListPage.getInitialProps = async (ctx: App.ReduxNextPageContext<TReviewListQuery>) => {
  const {
    store: { dispatch },
    query,
    asPath,
  } = ctx;

  const [pathname] = asPath.split('?');

  const { type, organization } = query;
  const product = query.product === PRODUCT_TYPE.VALJUTY ? PRODUCT_TYPE.OBMEN_VALJUTY : query.product;

  dispatch(
    serverDataDispatcher(ctx.req, {
      __ORGANIZATION__: setOrganizationByAlias,
    }),
  );

  const organizationDetails = ctx.req?.__ORGANIZATION__;
  const products = ctx.req?.__REVIEWS_FILTERS__?.allProducts;

  let typePage = getReviewType(type, products);
  const reviewObjectType = typePage.typeForService;

  if (isServer && organization && isEmpty(organizationDetails)) {
    if (!(await handleOrganizationRedirect(query.organization, ctx))) {
      throw createApplicationError(404, 'Not found');
    }
  }

  if (isServer && (!reviewObjectType || (product && EXCLUDE_PRODUCT_TYPES.includes(product)))) {
    throw createApplicationError(404, 'Not found');
  }

  dispatch(
    serverDataDispatcher(ctx.req, {
      __REVIEWS__: setReviewsList,
      __ORGANIZATIONS__: setOrganizationsList,
      __AD_PRODUCTS__: setAdProducts,
      __ORGANIZATION_RATING__: (data) => setOrganizationRating({ id: organizationDetails.id, data }),
      __ORGANIZATION_POSITION__: (data) => setOrganizationPosition({ id: organizationDetails.id, data }),
      __REVIEWS_COUNT__: setReviewsCount,
      __MENU__: setMenu,
      __REVIEWS_STATS__: setReviewsStats,
      __SPECIFIC_PRODUCT__: setProductDetails,
      __REVIEWS_QUERY__: setListQuery,
      __RATING_SRAVNI__: setRatingSravni,
      __SERVICES_RATING__: setServicesRating,
      __PRODUCT_RATING__: setProductsRating,
      __REVIEWS_SQUEEZE__: setReviewsSqueeze,
      __TOTAL_ACTIVE_ORGANIZATION_COUNT__: setActiveOrganizationCount,
    }),
  );

  await dispatch(fetchSeoIfNeeded(query, pathname, 'list'));
};

export default ListPage;
